import { MarketSelectionStatusIndicator } from '@src/components/StatusIndicator';
import { MarketDataStatusTable } from '@src/components/StatusTable';
import {
  getTypedValues,
  MarketAccountTypeEnum,
  Popover,
  Text,
  useMarketAccountsContext,
  useMarketsContext,
  usePopoverState,
  Z_INDEX,
  type KeyedMarketDataSnapshot,
  type Market,
  type MarketAccount,
} from '@talos/kyoko';
import type { ICellRendererParams } from 'ag-grid-community';
import { useMemo, useRef } from 'react';
import { of } from 'rxjs';
import type { MarketSelectorValue } from '../../../../MarketSelector/types';
import { PRICE_GRID_MARKET_DATA_COLUMN_TYPE } from '../../../columns/constants';
import { getLegIndexFromPriceGridMarketDataColumnField } from '../../../columns/priceGridMarketDataColumnUtils';
import type { PriceGridSecurityMarketDataColumnParams } from '../../../columns/types';
import { getPriceGridColumnParamsFromColDef } from '../../../columns/utils';
import type { PriceGridEntity } from '../../../PriceGridEntity';
import { getMultilegLegMarketAccounts, getMultilegLegMarkets } from '../common/getMultilegLegMarketDetails';
import { getPriceGridColumnMarketSelectorValue } from '../common/getPriceGridColumnMarketSelectorValue';
import { getPriceGridMarketDataSnapshotForCell } from '../common/getPriceGridMarketDataSnapshotForCell';
import { getPriceGridMarketSelectorSelections } from '../common/getPriceGridMarketSelectorSelections';

type StablePriceGridMarketDataSnapshot = {
  rowID: string;
  columnId: string;
  snapshot: KeyedMarketDataSnapshot;
};
type MarketStatusEntry = [string, NonNullable<KeyedMarketDataSnapshot['Markets']>[string]];

const INVALID_MARKET_ACCOUNTS_WARNING = 'Selected Market Accounts are not valid for the symbol.';

export function getStablePriceGridMarketDataSnapshot({
  snapshot,
  previousSnapshot,
  rowID,
  columnId,
}: {
  snapshot: KeyedMarketDataSnapshot | undefined;
  previousSnapshot: StablePriceGridMarketDataSnapshot | undefined;
  rowID: string | undefined;
  columnId: string | undefined;
}): KeyedMarketDataSnapshot | undefined {
  if (snapshot) {
    return snapshot;
  }

  if (previousSnapshot && previousSnapshot.rowID === rowID && previousSnapshot.columnId === columnId) {
    return previousSnapshot.snapshot;
  }

  return undefined;
}

export function getPriceGridMarketStatusSelections({
  marketSelectorValue,
  selectedRequestMarkets,
  securityMarkets,
  legMarketAccounts,
  marketsByName,
  marketAccountsByName,
  marketAccountsByMarket,
}: {
  marketSelectorValue: MarketSelectorValue | undefined;
  selectedRequestMarkets: string[] | undefined;
  securityMarkets: string[] | undefined;
  legMarketAccounts?: string[];
  marketsByName: ReadonlyMap<string, unknown>;
  marketAccountsByName: ReadonlyMap<string, unknown>;
  marketAccountsByMarket: ReadonlyMap<string, MarketAccount[]>;
}) {
  const selections = getPriceGridMarketSelectorSelections(
    marketSelectorValue ??
      (selectedRequestMarkets ? { type: 'custom', selections: selectedRequestMarkets } : undefined),
    marketsByName,
    marketAccountsByName
  );
  if (selections.selectedMarkets.length > 0 || selections.selectedMarketAccounts.length > 0) {
    return selections;
  }

  if (legMarketAccounts && legMarketAccounts.length > 0) {
    return { selectedMarkets: [], selectedMarketAccounts: legMarketAccounts };
  }

  const selectedMarkets: string[] = [];
  const selectedMarketAccounts: string[] = [];

  for (const market of securityMarkets ?? []) {
    const tradingMarketAccounts = marketAccountsByMarket
      .get(market)
      ?.filter(marketAccount => marketAccount.Type === MarketAccountTypeEnum.Trading);

    if (tradingMarketAccounts && tradingMarketAccounts.length > 0) {
      selectedMarketAccounts.push(...tradingMarketAccounts.map(marketAccount => marketAccount.Name));
    } else if (marketsByName.has(market)) {
      selectedMarkets.push(market);
    }
  }

  return { selectedMarkets, selectedMarketAccounts };
}

export function getPriceGridMarketStatusWarning({
  selectedMarketAccounts,
  securityMarkets,
  marketAccountsByName,
}: {
  selectedMarketAccounts: string[];
  securityMarkets: string[] | undefined;
  marketAccountsByName: ReadonlyMap<string, Pick<MarketAccount, 'Market'> | undefined>;
}) {
  if (selectedMarketAccounts.length === 0) {
    return undefined;
  }

  const securityMarketsSet = new Set(securityMarkets ?? []);
  const hasValidMarketAccount = selectedMarketAccounts.some(marketAccount =>
    securityMarketsSet.has(marketAccountsByName.get(marketAccount)?.Market ?? '')
  );

  return hasValidMarketAccount ? undefined : INVALID_MARKET_ACCOUNTS_WARNING;
}

export function getPriceGridMarketDataStatuses(snapshot: KeyedMarketDataSnapshot | undefined) {
  return getTypedValues(snapshot?.Markets ?? {});
}

function getMarketStatusSortLabel({
  entry,
  marketsByName,
  marketAccountsByName,
}: {
  entry: MarketStatusEntry;
  marketsByName: ReadonlyMap<string, Pick<Market, 'DisplayName'> | undefined>;
  marketAccountsByName: ReadonlyMap<string, Pick<MarketAccount, 'DisplayName'> | undefined>;
}) {
  const [key, status] = entry;
  const market = status.Market ?? key;
  const marketAccount = status.MarketAccount ?? key;

  return (
    marketAccountsByName.get(marketAccount)?.DisplayName ??
    marketsByName.get(market)?.DisplayName ??
    marketAccount ??
    market ??
    key
  ).toLocaleLowerCase();
}

function compareMarketStatusEntries(
  a: MarketStatusEntry,
  b: MarketStatusEntry,
  {
    marketsByName,
    marketAccountsByName,
  }: {
    marketsByName: ReadonlyMap<string, Pick<Market, 'DisplayName'> | undefined>;
    marketAccountsByName: ReadonlyMap<string, Pick<MarketAccount, 'DisplayName'> | undefined>;
  }
) {
  return getMarketStatusSortLabel({
    entry: a,
    marketsByName,
    marketAccountsByName,
  }).localeCompare(
    getMarketStatusSortLabel({
      entry: b,
      marketsByName,
      marketAccountsByName,
    })
  );
}

export function getPriceGridMarketStatusSnapshot({
  snapshot,
  selectedMarkets,
  selectedMarketAccounts,
  marketsByName,
  marketAccountsByName,
}: {
  snapshot: KeyedMarketDataSnapshot | undefined;
  selectedMarkets: string[];
  selectedMarketAccounts: string[];
  marketsByName: ReadonlyMap<string, Pick<Market, 'DisplayName'> | undefined>;
  marketAccountsByName: ReadonlyMap<string, Pick<MarketAccount, 'DisplayName' | 'Market'> | undefined>;
}): KeyedMarketDataSnapshot | undefined {
  if (!snapshot) {
    return undefined;
  }

  if (selectedMarkets.length === 0 && selectedMarketAccounts.length === 0) {
    return {
      ...snapshot,
      Markets: Object.fromEntries(
        Object.entries(snapshot.Markets ?? {}).toSorted((a, b) =>
          compareMarketStatusEntries(a, b, { marketsByName, marketAccountsByName })
        )
      ),
    };
  }

  const selectedMarketsSet = new Set(selectedMarkets);
  const selectedMarketAccountsSet = new Set(selectedMarketAccounts);
  const selectedAccountMarkets = new Set(
    selectedMarketAccounts.map(marketAccount => marketAccountsByName.get(marketAccount)?.Market).filter(Boolean)
  );
  const markets = Object.fromEntries(
    Object.entries(snapshot.Markets ?? {})
      .filter(([key, status]) => {
        const market = status.Market ?? key;
        const marketAccount = status.MarketAccount ?? key;

        return (
          selectedMarketsSet.has(key) ||
          selectedMarketsSet.has(market) ||
          selectedMarketAccountsSet.has(key) ||
          selectedMarketAccountsSet.has(marketAccount) ||
          selectedAccountMarkets.has(key) ||
          selectedAccountMarkets.has(market)
        );
      })
      .toSorted((a, b) => compareMarketStatusEntries(a, b, { marketsByName, marketAccountsByName }))
  );

  return { ...snapshot, Markets: markets };
}

export function PriceGridMarketStatusIndicator({
  params,
}: {
  params: Pick<ICellRendererParams<PriceGridEntity>, 'colDef' | 'column' | 'data'>;
}) {
  const { marketsByName } = useMarketsContext();
  const { marketAccountsByName, marketAccountsByMarket } = useMarketAccountsContext();
  const marketSelectorValue = getPriceGridColumnMarketSelectorValue(params);
  const selectedRequestMarkets = getPriceGridColumnParamsFromColDef<PriceGridSecurityMarketDataColumnParams>(
    params.colDef,
    PRICE_GRID_MARKET_DATA_COLUMN_TYPE
  )?.Markets;
  const legIndex = getLegIndexFromPriceGridMarketDataColumnField(params.colDef?.field);
  const securityMarkets = params.data?.security
    ? legIndex != null
      ? getMultilegLegMarkets({ dataSecurity: params.data.security, legIndex })
      : params.data.security.Markets
    : undefined;
  const legMarketAccounts = params.data?.security
    ? getMultilegLegMarketAccounts({ dataSecurity: params.data.security, legIndex })
    : undefined;
  const { selectedMarkets, selectedMarketAccounts } = getPriceGridMarketStatusSelections({
    marketSelectorValue,
    selectedRequestMarkets,
    marketsByName,
    marketAccountsByName,
    marketAccountsByMarket,
    securityMarkets,
    legMarketAccounts,
  });
  const warning = getPriceGridMarketStatusWarning({
    selectedMarketAccounts,
    securityMarkets,
    marketAccountsByName,
  });
  const snapshot = getPriceGridMarketDataSnapshotForCell(params);
  const previousSnapshotRef = useRef<StablePriceGridMarketDataSnapshot>();
  const rowID = params.data?.rowID;
  const columnId = params.column?.getColId();
  const stableSnapshot = getStablePriceGridMarketDataSnapshot({
    snapshot,
    previousSnapshot: previousSnapshotRef.current,
    rowID,
    columnId,
  });

  if (snapshot && rowID && columnId) {
    previousSnapshotRef.current = {
      rowID,
      columnId,
      snapshot,
    };
  }

  const statusSnapshot = useMemo(
    () =>
      getPriceGridMarketStatusSnapshot({
        snapshot: stableSnapshot,
        selectedMarkets,
        selectedMarketAccounts,
        marketsByName,
        marketAccountsByName,
      }),
    [marketAccountsByName, marketsByName, selectedMarketAccounts, selectedMarkets, stableSnapshot]
  );
  const statuses = useMemo(() => getPriceGridMarketDataStatuses(statusSnapshot), [statusSnapshot]);
  const snapshots = useMemo(() => (statusSnapshot ? of(statusSnapshot) : undefined), [statusSnapshot]);
  const statusWrapperProps = { 'data-testid': 'price-grid-market-status' };

  const popover = usePopoverState({
    usePortal: true,
    trigger: 'hover',
    placement: 'bottom',
    isSmall: true,
    delay: undefined,
  });

  const statusIndicator = (
    <MarketSelectionStatusIndicator
      selectedMarkets={selectedMarkets}
      selectedMarketAccounts={selectedMarketAccounts}
      statuses={statuses}
    />
  );

  const hasStatusTableContent = Boolean(snapshots && Object.keys(statusSnapshot?.Markets ?? {}).length > 0);

  if (hasStatusTableContent && snapshots) {
    return (
      <span {...statusWrapperProps}>
        <Popover {...popover} zIndex={Z_INDEX.dropdown}>
          {statusIndicator}
          {warning ? <Text>{warning}</Text> : <MarketDataStatusTable snapshots={snapshots} />}
        </Popover>
      </span>
    );
  }

  if (warning) {
    return (
      <span {...statusWrapperProps}>
        <Popover {...popover} zIndex={Z_INDEX.dropdown}>
          {statusIndicator}
          <Text>{warning}</Text>
        </Popover>
      </span>
    );
  }

  return <span {...statusWrapperProps}>{statusIndicator}</span>;
}
